/**
 * PostHog product analytics + session replay.
 * No-ops when REACT_APP_POSTHOG_KEY is missing (local/staging without a key).
 */
import posthog from "posthog-js";

const KEY = String(process.env.REACT_APP_POSTHOG_KEY || "").trim();
const HOST = String(process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com").trim();

let initialized = false;

export function initPostHog() {
  if (initialized || typeof window === "undefined") return;
  if (!KEY) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.info("[posthog] Skipped init; REACT_APP_POSTHOG_KEY not set.");
    }
    return;
  }

  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    // Session replay on by default. Passwords are masked; card inputs use type=password-like masking via selectors.
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      // Avoid recording Stripe iframe internals / card number fields if they appear in DOM.
      maskTextSelector: "[data-ph-mask], .StripeElement, iframe[name^='__privateStripe']",
    },
    loaded: (ph) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.info("[posthog] Initialized; session replay enabled.");
      }
      // Keep a handle for quick console debugging in local/staging.
      window.__posthog = ph;
    },
  });

  initialized = true;
}

export function identifyUser({ email, name, subscriptionStatus } = {}) {
  if (!initialized || !KEY) return;
  const distinctId = String(email || "").trim().toLowerCase();
  if (!distinctId) return;

  const props = {};
  if (name) props.name = String(name).trim();
  if (email) props.email = String(email).trim().toLowerCase();
  if (subscriptionStatus) props.subscription_status = String(subscriptionStatus);

  posthog.identify(distinctId, props);
}

export function resetUser() {
  if (!initialized || !KEY) return;
  posthog.reset();
}

export function captureEvent(event, properties) {
  if (!initialized || !KEY) return;
  posthog.capture(event, properties);
}

export function getPostHog() {
  return initialized && KEY ? posthog : null;
}
